import { lazy, Suspense, StrictMode, useState, useEffect } from "react";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { CssBaseline } from "@mui/material";

// Keep only critical imports that are needed immediately
import { NavLayout } from "./layouts/NavLayout";
import { ROUTES_XLOGS } from "./constants/URLs";
import { CalendarContextProvider } from "./views/calendar/context/context";
import AdminProtectedRoute from "./AdminProtectedRoute";
import GoodFaithEffortProvider from "./views/admin/views/good_faith_effort/context/provider/gfe_provider";
import MsbProtectedRoute from "./MsbProtectedRoute";
// Passthrough chrome for the reused profile under the admin detail route. Its own tiny module, so
// importing it eagerly (needed for the route's `chrome` prop) does NOT pull the whole
// student-management feature into the main bundle — the layout + directory page are lazy below.
import { StudentProfileNoChrome } from "./views/admin/views/student_management/student_profile_no_chrome";
import GlobalSpeechToText from "./design/high-level/speech_to_text/GlobalSpeechToText";
import { useXNGDispatch, useXNGSelector } from "./context/store";
import { selectFeatureFlags } from "./context/slices/featureFlagsSlice";
import { THIRTY_DAY_REVIEW_FLAG } from "./views/thirty_day_review/constants";
import { selectStateInUS } from "./context/slices/stateInUsSlice";
import { selectServiceProviderProfile } from "./context/slices/serviceProviderProfileSlice";
import { selectLoggedInClientAssignment } from "./context/slices/userProfileSlice";
import { setHighlightLocationCard } from "./context/slices/campusHighlightSlice";
import { cookieExists } from "./utils/cookies";
import sessionStorageKeys from "./browser/sessionStorageKeys";
import {setAutoFreeze} from "immer"
setAutoFreeze(false); // Disable auto-freeze to prevent performance issues with large state trees
// Lazy load all views and heavy components
const PageNotFound = lazy(() => import("./views/404"));
const StudentManager = lazy(() => import("./views/students/views/manager/manager"));
const XLogsCalendar = lazy(() => import("./views/calendar/calendar"));
const Notator = lazy(() => import("./views/notator/notator"));
const ThirtyDayReviewPage = lazy(() => import("./views/thirty_day_review/thirty_day_review_page"));
const UserProfile = lazy(() => import("./views/user/profile"));
const NotatorToolsProvider = lazy(() => import("./views/notator/tools").then(m => ({ default: m.NotatorToolsProvider })));
const UnpostedSessionsLayout = lazy(() => import("./views/unposted_sessions/unposted_sessions_layout"));
const UnpostedSessionsBatchPostProvider = lazy(() => import("./views/unposted_sessions/batch-post/providers/unposted_sessions_batch_post_provider"));
const UnpostedSessionsBatchPostView = lazy(() => import("./views/unposted_sessions/batch-post/unposted_sessions_batch_post_view"));
const UserApprovalView = lazy(() => import("./views/admin/views/user_management/views/user_approvals/veiws/user_approval_view"));
const UserApprovalsProvider = lazy(() => import("./views/admin/views/user_management/views/user_approvals/context/userApprovalsProvider"));
const StaffDirectoryManagerLayout = lazy(() => import("./views/admin/views/user_management/views/staff_directory_manager/staff_directory_manager_layout"));
const UserManagementLayout = lazy(() => import("./views/admin/views/user_management/user_management_layout"));
const UserManagementProvider = lazy(() => import("./views/admin/views/user_management/context/provider/user_management_provider"));
const StaffDirectoryManagerHomePage = lazy(() => import("./views/admin/views/user_management/views/staff_directory_manager/views/staff_directory_manager_home_page"));
const StaffDirectoryManagerProfilePage = lazy(() => import("./views/admin/views/user_management/views/staff_directory_manager/views/profile_page.tsx/staff_directory_manager_profile_page"));
const CampusInformation = lazy(() => import("./views/admin/views/campus_info/campus_info"));
const StaffDirectoryHomePageProvider = lazy(() => import("./views/admin/views/user_management/views/staff_directory_manager/context/staff_directory_home_page_provider"));
const BuildInfo = lazy(() => import("./views/BuildInfo"));
const StaticReportsView = lazy(() => import("./views/reports/views/static_reports/static_reports_view"));
const FortitudeView = lazy(() => import("./fortitude/docs"));
const ReportsOutlet = lazy(() => import("./views/reports/reports_outlet"));
const AccountStudentReport = lazy(() => import("./views/reports/views/account_student_report/account_student_report").then(m => ({ default: m.AccountStudentReport })));
const StudentMerge = lazy(() => import("./views/admin/views/student_merge/student_merge"));
const BillableServices = lazy(() => import("./views/admin/views/billable_services/billable_services"));
const TransportationAccommodationsAdmin = lazy(() => import("./views/admin/views/transportation_accommodations/transportation_accommodations_admin"));
const GoodFaithEffortTracking = lazy(() => import("./views/admin/views/good_faith_effort/views"));
const ImportsLayout = lazy(() => import("./views/admin/views/imports/imports_layout"));
const BillingCatalog = lazy(() => import("./views/admin/views/billing_catalog/billing_catalog"));
const BillingRates = lazy(() => import("./views/admin/views/billing_rates/billing_rates"));
const BillingTest = lazy(() => import("./views/admin/views/billing_test/billing_test"));
const BillingWorklist = lazy(() => import("./views/admin/views/billing_worklist/billing_worklist"));
const MsbOverview = lazy(() => import("./views/msb/overview/msb_overview"));
const EdiGlossary = lazy(() => import("./views/msb/reference/edi_glossary"));
const Bundles = lazy(() => import("./views/msb/bundles/bundles"));
const EdiFiles = lazy(() => import("./views/msb/edi_files/edi_files"));
const ClaimExplorer = lazy(() => import("./views/msb/claims/claim_explorer"));
const ClaimDetailPage = lazy(() => import("./views/msb/claims/claim_detail_page"));
const DenialWorklist = lazy(() => import("./views/msb/worklists/denial_worklist"));
const RemittanceExplorer = lazy(() => import("./views/msb/remittances/remittance_explorer"));
const RemittanceDetailPage = lazy(() => import("./views/msb/remittances/remittance_detail_page"));
const IntakeReviews = lazy(() => import("./views/msb/worklists/intake_reviews"));
const CorrectionWorklists = lazy(() => import("./views/msb/worklists/correction_worklists"));
const Districts = lazy(() => import("./views/msb/districts/districts"));
const DistrictDetailPage = lazy(() => import("./views/msb/districts/district_detail_page"));
const ConfigurationKeys = lazy(() => import("./views/msb/configuration/configuration_keys"));
const ConfigurationKey = lazy(() => import("./views/msb/configuration/configuration_key"));
const BillingExclusions = lazy(() => import("./views/msb/billing_exclusions/billing_exclusions"));
const DistrictProfile = lazy(() => import("./views/admin/views/district_profile").then(m => ({ default: m.DistrictProfile })));
const FinancialDashboardProvider = lazy(() => import("./views/admin/views/district_profile/context/provider/financialDashboardProvider").then(m => ({ default: m.FinancialDashboardProvider })));
const UnpostedSessionsProvider = lazy(() => import("./views/unposted_sessions/context/unposted_sessions_provider"));
const UnpostedSessionsView = lazy(() => import("./views/unposted_sessions/views/unposted_sessions_view"));
const UnpostedSessionsNotatorView = lazy(() => import("./views/unposted_sessions/views/unposted_sessions_view_notator_view"));
const PrescribingProvider = lazy(() => import("./views/admin/views/prescibing_provider").then(m => ({ default: m.PrescribingProvider })));
const PrescribingProviderContextProvider = lazy(() => import("./views/admin/views/prescibing_provider/context/provider/PrescribingProviderContextProvider"));
const ProgressReporting = lazy(() => import("./views/reports/views/progress_reporting").then(m => ({ default: m.ProgressReporting })));
const ProgressSummary = lazy(() => import("./views/reports/views/progress_reporting/progress_summary_view").then(m => ({ default: m.ProgressSummary })));
const ProgressReportingContextProvider = lazy(() => import("./views/reports/views/progress_reporting/context/provider/ProgressReportingContextProvider"));
const ProviderNotFoundError = lazy(() => import("./design/high-level/Error/ProviderNotFoundError"));
const ThankYouModal = lazy(() => import("./design/modal_templates/ThankYouModal"));
const RemoveProviderConfirmationModal = lazy(() => import("./design/modal_templates/RemoveProviderConfirmationModal"));
const SimpleAttentionModal = lazy(() => import("./design/modal_templates/SimpleAttentionModal").then(m => ({ default: m.SimpleAttentionModal })));
const ErrorEntriesModal = lazy(() => import("./modals/error_entries_modal").then(m => ({ default: m.ErrorEntriesModal })));
const SessionLogsProvider = lazy(() => import("./views/reports/views/session_logs/context").then(m => ({ default: m.SessionLogsProvider })));
const ServiceReportView = lazy(() => import("./views/reports/views/session_logs/views").then(m => ({ default: m.ServiceReportView })));
const SessionLogsLayout = lazy(() => import("./views/reports/views/session_logs/views").then(m => ({ default: m.SessionLogsLayout })));
const SessionLogsView = lazy(() => import("./views/reports/views/session_logs/views").then(m => ({ default: m.SessionLogsView })));
const UnpostedSessionsReportView = lazy(() => import("./views/reports/views/unposted_sessions_report/unposted_sessions_report_view"));
const StudentsTabLayout = lazy(() => import("./views/students/views/students_tab_layout"));
const StudentProfileContextProvider = lazy(() => import("./views/students/views/student_profile/context/student_profile_context_provider"));
const StudentManagementLayout = lazy(() => import("./views/admin/views/student_management/student_management_layout"));
const StudentDirectoryPage = lazy(() => import("./views/admin/views/student_management/student_directory_page"));
const UnassignedStudentsView = lazy(() => import("./views/admin/views/unassigned_students/unassigned_students_view"));
const StudentProfileSuspenseBoundary = lazy(() => import("./views/students/views/student_profile/components/student_profile_suspense_boundary"));
const CaseloadManagementProvider = lazy(() => import("./views/admin/views/caseload_management/context/caseload_management_provider"));
const CaseloadManagementLayout = lazy(() => import("./views/admin/views/caseload_management/caseload_management_layout"));
const ApproverCaseloadsView = lazy(() => import("./views/admin/views/caseload_management/views/approver_caseloads"));
const DecCaseLoadView = lazy(() => import("./views/admin/views/caseload_management/views/dec_caseloads"));
const SchoolCalendarView = lazy(() => import("./views/admin/views/calendar_management/views/school_calendar/school_calendar_view"));
const InstanceReportLayout = lazy(() => import("./views/reports/views/instance_report/views/instance_report_layout"));
const InstanceReportProvider = lazy(() => import("./views/reports/views/instance_report/context/instance_report_provider"));
const InstanceReportView = lazy(() => import("./views/reports/views/instance_report/views/instance_report_view"));
const SessionLogsV2Layout = lazy(() => import("./views/reports/views/session_logs_v2/views").then(m => ({ default: m.SessionLogsV2Layout })));
const SessionLogsV2View = lazy(() => import("./views/reports/views/session_logs_v2/views").then(m => ({ default: m.SessionLogsV2View })));
const SessionLogsV2ReportView = lazy(() => import("./views/reports/views/session_logs_v2/views").then(m => ({ default: m.SessionLogsV2ReportView })));
const MyExportsView = lazy(() => import("./views/reports/views/session_logs_v2/views").then(m => ({ default: m.MyExportsView })));
const MyExportsLayout = lazy(() => import("./views/reports/views/session_logs_v2/views").then(m => ({ default: m.MyExportsLayout })));
const AccountStudentsV2Layout = lazy(() => import("./views/reports/views/account_student_report_v2/views").then(m => ({ default: m.AccountStudentsV2Layout })));
const AccountStudentsV2View = lazy(() => import("./views/reports/views/account_student_report_v2/views").then(m => ({ default: m.AccountStudentsV2View })));

const UnpostedSessionsV2Layout = lazy(() => import("./views/reports/views/unposted_sessions_v2/views").then(m => ({ default: m.UnpostedSessionsV2Layout })));
const UnpostedSessionsV2View = lazy(() => import("./views/reports/views/unposted_sessions_v2/views").then(m => ({ default: m.UnpostedSessionsV2View })));

const StudentProgressV2Layout = lazy(() => import("./views/reports/views/student_progress_v2/views").then(m => ({ default: m.StudentProgressV2Layout })));
const StudentProgressV2View = lazy(() => import("./views/reports/views/student_progress_v2/views").then(m => ({ default: m.StudentProgressV2View })));
const StudentProgressV2SummaryView = lazy(() => import("./views/reports/views/student_progress_v2/views").then(m => ({ default: m.StudentProgressV2SummaryView })));
const StudentProgressV2EditView = lazy(() => import("./views/reports/views/student_progress_v2/views").then(m => ({ default: m.StudentProgressV2EditView })));
const ThirtyDayReviewReportLayout = lazy(() => import("./views/reports/views/thirty_day_review_report/views").then(m => ({ default: m.ThirtyDayReviewReportLayout })));
const ThirtyDayReviewReportView = lazy(() => import("./views/reports/views/thirty_day_review_report/views").then(m => ({ default: m.ThirtyDayReviewReportView })));
const ThirtyDayReviewReportResultsView = lazy(() => import("./views/reports/views/thirty_day_review_report/views").then(m => ({ default: m.ThirtyDayReviewReportResultsView })));


const RMTSUpdateModal = lazy(() => import("./design/modal_templates/RMTSUpdateModal"));
const RMTS_MODAL_STORAGE_KEY = "xng_has_seen_rmts_update_2026";

const CampusSelectionModal = lazy(() => import("./design/modal_templates/CampusSelectionModal"));
const CAMPUS_MODAL_SESSION_KEY = sessionStorageKeys.CAMPUS_MODAL_DISMISSED_KEY;

function App() {
  const [showRMTSModal, setShowRMTSModal] = useState(false);
  const stateInUs = useXNGSelector(selectStateInUS);
  const thirtyDayReviewEnabled = useXNGSelector((s) => selectFeatureFlags(s, THIRTY_DAY_REVIEW_FLAG));
  const billingPrototypeActive = useXNGSelector((s) => selectFeatureFlags(s, "BillingPrototypeActive"));

  useEffect(() => {
    if (stateInUs === "TX") {
      const hasSeen = localStorage.getItem(RMTS_MODAL_STORAGE_KEY);
      if (!hasSeen) {
        setShowRMTSModal(true);
      }
    }
  }, [stateInUs]);

  const handleCloseRMTSModal = () => {
    setShowRMTSModal(false);
    localStorage.setItem(RMTS_MODAL_STORAGE_KEY, "true");
  };

  const navigate = useNavigate();
  const dispatch = useXNGDispatch();
  const serviceProviderProfile = useXNGSelector(selectServiceProviderProfile);
  const loggedInClientAssignment = useXNGSelector(selectLoggedInClientAssignment);
  const [showCampusModal, setShowCampusModal] = useState(false);

  // Reaching App means the user is logged in and fully onboarded (past the
  // registration/onboarding/signatory gates). Prompt them to set a campus if
  // none is assigned, unless they're an Executive Admin, impersonating another
  // user, or already dismissed the prompt this session.
  useEffect(() => {
    const dismissed = sessionStorage.getItem(CAMPUS_MODAL_SESSION_KEY);
    const isProxyLogin = cookieExists("loggedInAsUserID");
    const hasNoCampus = !serviceProviderProfile?.activeSchoolCampuses?.length;

    if (
      !dismissed &&
      !isProxyLogin &&
      !!serviceProviderProfile &&
      hasNoCampus &&
      !loggedInClientAssignment.isExecutiveAdmin
    ) {
      setShowCampusModal(true);
    }
  }, [serviceProviderProfile, loggedInClientAssignment]);

  const handleUpdateCampus = () => {
    setShowCampusModal(false);
    dispatch(setHighlightLocationCard(true));
    navigate(ROUTES_XLOGS.user);
  };

  const handleRemindLater = () => {
    setShowCampusModal(false);
    sessionStorage.setItem(CAMPUS_MODAL_SESSION_KEY, "true");
  };

  return (
    <>
      <CssBaseline>
          <NavLayout>
          <CalendarContextProvider>
            <Suspense fallback={<div>Loading...</div>}>
              <Routes>
                <Route path={ROUTES_XLOGS.fortitude + "/*"} element={<FortitudeView />} />
                <Route
                  path={ROUTES_XLOGS.calendar}
                  element={<XLogsCalendar />}
                />
                <Route
                  path={ROUTES_XLOGS.notator + "/:sessionID"}
                  element={
                    <NotatorToolsProvider>
                      <Notator />
                    </NotatorToolsProvider>
                  }
                />
                <Route
                  path={ROUTES_XLOGS.thirtyDayReview + "/:sessionID"}
                  element={
                    thirtyDayReviewEnabled ? (
                      <ThirtyDayReviewPage />
                    ) : (
                      <Navigate to={ROUTES_XLOGS.calendar} replace />
                    )
                  }
                />

            <Route path={ROUTES_XLOGS.reports.index} element={<ReportsOutlet />}>
              <Route element={<StaticReportsView />} index />
              <Route
                path={ROUTES_XLOGS.reports.accountStudentReport}
                element={<AccountStudentReport />}
              />
              <Route
                path={ROUTES_XLOGS.reports.sessionLogs}
                element={
                  <SessionLogsProvider>
                    <SessionLogsLayout />
                  </SessionLogsProvider>
                }
              >
                <Route index element={<SessionLogsView />} />
                <Route path={ROUTES_XLOGS.reports.serviceReport} element={<ServiceReportView />} />
              </Route>
              <Route
                path={ROUTES_XLOGS.reports.unpostedSessionsReport}
                element={
                  <StrictMode>
                    <UnpostedSessionsReportView />
                  </StrictMode>
                }
              />
              <Route
                path={ROUTES_XLOGS.reports.instanceReport}
                element={
                  <StrictMode>
                    <InstanceReportProvider>
                      <InstanceReportLayout />
                    </InstanceReportProvider>
                  </StrictMode>
                }
              >
                <Route index element={<InstanceReportView />} />
              </Route>
              <Route
                path={ROUTES_XLOGS.reports.sessionLogsV2}
                element={<SessionLogsV2Layout />}
              >
                <Route index element={<SessionLogsV2View />} />
                <Route path={ROUTES_XLOGS.reports.sessionLogsV2Report} element={<SessionLogsV2ReportView />} />
              </Route>
              <Route
                path={ROUTES_XLOGS.reports.accountStudentReportV2}
                element={<AccountStudentsV2Layout />}
              >
                <Route index element={<AccountStudentsV2View />} />
              </Route>
              <Route
                path={ROUTES_XLOGS.reports.unpostedSessionsV2}
                element={<UnpostedSessionsV2Layout />}
              >
                <Route index element={<UnpostedSessionsV2View />} />
              </Route>
              <Route
                path={ROUTES_XLOGS.reports.studentProgressV2}
                element={<StudentProgressV2Layout />}
              >
                <Route index element={<StudentProgressV2View />} />
                <Route path={ROUTES_XLOGS.reports.studentProgressV2Summary} element={<StudentProgressV2SummaryView />} />
                <Route path={ROUTES_XLOGS.reports.studentProgressV2Edit} element={<StudentProgressV2EditView />} />
              </Route>
              <Route
                path={ROUTES_XLOGS.reports.thirtyDayReviewReport}
                element={<ThirtyDayReviewReportLayout />}
              >
                <Route index element={<ThirtyDayReviewReportView />} />
                <Route path={ROUTES_XLOGS.reports.thirtyDayReviewReportResults} element={<ThirtyDayReviewReportResultsView />} />
              </Route>
              <Route path={ROUTES_XLOGS.reports.myExports} element={<MyExportsLayout />}>
                <Route index element={<MyExportsView />} />
              </Route>
            </Route>

            <Route path={ROUTES_XLOGS._students.manager} element={<StudentsTabLayout />}>
              <Route
                path={ROUTES_XLOGS._students.profile}
                element={

                      <StrictMode>
                        <StudentProfileContextProvider/>
                      </StrictMode>


                }
              >
                <Route index element={<StudentProfileSuspenseBoundary />} />
              </Route>
              <Route index element={<StudentManager />} />
              {/* <Route path={ROUTES_XLOGS._students.groups} element={<StudentGroups />} /> */}
            </Route>

            <Route path={ROUTES_XLOGS.user} element={<UserProfile />} />
            <Route element={<AdminProtectedRoute />}>
              <Route
                index
                path={ROUTES_XLOGS._admin.mydistrictProfile}
                element={
                  <FinancialDashboardProvider>
                    <DistrictProfile />
                  </FinancialDashboardProvider>
                }
              />
              <Route
                path={ROUTES_XLOGS._admin.index}
                element={
                  <UserManagementProvider>
                    <UserManagementLayout />
                  </UserManagementProvider>
                }
              >
                <Route
                  index
                  path={ROUTES_XLOGS._admin.userApproval}
                  element={
                    <UserApprovalsProvider>
                      <UserApprovalView />
                    </UserApprovalsProvider>
                  }
                />
                <Route
                  path={ROUTES_XLOGS._admin.staffDirectoryManager}
                  element={
                    <StaffDirectoryHomePageProvider>
                      <StaffDirectoryManagerLayout />
                    </StaffDirectoryHomePageProvider>
                  }
                >
                  <Route index element={<StaffDirectoryManagerHomePage />} />
                  <Route
                    path={ROUTES_XLOGS._admin.staffDirectoryManager + "/:provider_info_id"}
                    element={<StaffDirectoryManagerProfilePage />}
                  />
                </Route>
              </Route>
              <Route
                path={ROUTES_XLOGS._admin.studentManagement}
                element={<StudentManagementLayout />}
              >
                <Route index element={<StudentDirectoryPage />} />
                <Route
                  path={ROUTES_XLOGS._admin.studentManagement + "/:studentID"}
                  element={
                    <StrictMode>
                      <StudentProfileContextProvider
                        chrome={StudentProfileNoChrome}
                        headerVariant="admin"
                        backLabel="Student Management"
                        backTo={ROUTES_XLOGS._admin.studentManagement}
                      />
                    </StrictMode>
                  }
                >
                  <Route index element={<StudentProfileSuspenseBoundary />} />
                </Route>
              </Route>
              <Route
                path={ROUTES_XLOGS._admin.unassignedStudents}
                element={<UnassignedStudentsView />}
              />
              <Route path={ROUTES_XLOGS._admin.campusManagement} element={<CampusInformation />} />
              <Route path={ROUTES_XLOGS._admin.studentMerge} element={<StudentMerge />} />
              <Route
                path={ROUTES_XLOGS._admin.billableServices}
                element={<BillableServices />}
              />
              <Route
                path={ROUTES_XLOGS._admin.transportationAccommodations}
                element={<TransportationAccommodationsAdmin />}
              />
              <Route
                path={ROUTES_XLOGS._admin.goodFaithEffort}
                element={
                  <GoodFaithEffortProvider>
                    <GoodFaithEffortTracking />
                  </GoodFaithEffortProvider>
                }
              />
              <Route path={ROUTES_XLOGS._admin.schoolCalendar} element={<SchoolCalendarView />} />
              {/* The district billing worklist (XLOGS-195): registered only while the flag is on. */}
              {billingPrototypeActive && (
                <Route path={ROUTES_XLOGS._admin.billingWorklist} element={<BillingWorklist />} />
              )}
              <Route
                index
                path={ROUTES_XLOGS._admin.prescribingProvider}
                element={
                  <UserManagementProvider>
                    <PrescribingProviderContextProvider>
                      <PrescribingProvider />
                    </PrescribingProviderContextProvider>
                  </UserManagementProvider>
                }
              />
              <Route
                path={ROUTES_XLOGS._admin.index}
                element={
                  <CaseloadManagementProvider>
                    <CaseloadManagementLayout />
                  </CaseloadManagementProvider>
                }
              >
                <Route
                  path={ROUTES_XLOGS._admin.approverCaseloads}
                  element={<ApproverCaseloadsView />}
                />
                <Route path={ROUTES_XLOGS._admin.decCaseloads} element={<DecCaseLoadView />} />
              </Route>
            </Route>
            {/* The account-agnostic MSB operator zone (XLOGS-205) — msb_role gate only. */}
            <Route element={<MsbProtectedRoute />}>
              <Route path={ROUTES_XLOGS._msb.index} element={<MsbOverview />} />
              <Route path={ROUTES_XLOGS._msb.bundles} element={<Bundles />} />
              <Route path={ROUTES_XLOGS._msb.ediFiles} element={<EdiFiles />} />
              <Route path={ROUTES_XLOGS._msb.claims} element={<ClaimExplorer />} />
              <Route path={ROUTES_XLOGS._msb.claimDetail} element={<ClaimDetailPage />} />
              <Route path={ROUTES_XLOGS._msb.denials} element={<DenialWorklist />} />
              <Route path={ROUTES_XLOGS._msb.remittances} element={<RemittanceExplorer />} />
              <Route path={ROUTES_XLOGS._msb.remittanceDetail} element={<RemittanceDetailPage />} />
              <Route path={ROUTES_XLOGS._msb.intakeReviews} element={<IntakeReviews />} />
              <Route path={ROUTES_XLOGS._msb.corrections} element={<CorrectionWorklists />} />
              <Route path={ROUTES_XLOGS._msb.districts} element={<Districts />} />
              <Route path={ROUTES_XLOGS._msb.districtDetail} element={<DistrictDetailPage />} />
              <Route path={ROUTES_XLOGS._msb.billingExclusions} element={<BillingExclusions />} />
              <Route path={ROUTES_XLOGS._msb.configuration} element={<ConfigurationKeys />} />
              <Route path={ROUTES_XLOGS._msb.configurationKey} element={<ConfigurationKey />} />
              <Route path={ROUTES_XLOGS._msb.imports} element={<ImportsLayout />} />
              <Route path={ROUTES_XLOGS._msb.billingCatalog} element={<BillingCatalog />} />
              <Route path={ROUTES_XLOGS._msb.billingRates} element={<BillingRates />} />
              <Route path={ROUTES_XLOGS._msb.ediGlossary} element={<EdiGlossary />} />
              <Route path={ROUTES_XLOGS._msb.billingTest} element={<BillingTest />} />
            </Route>
            <Route
              path={ROUTES_XLOGS.unposted_sessions.index}
              element={
                <UnpostedSessionsProvider>
                  <UnpostedSessionsLayout />
                </UnpostedSessionsProvider>
              }
            >
              <Route
                path={ROUTES_XLOGS.unposted_sessions.index + "/:viewId"}
                element={<UnpostedSessionsView />}
              />
              <Route
                path={ROUTES_XLOGS.unposted_sessions.notator}
                element={<UnpostedSessionsNotatorView />}
              >
                <Route
                  index
                  element={
                    <NotatorToolsProvider>
                      <Notator />
                    </NotatorToolsProvider>
                  }
                />
              </Route>
            </Route>
            <Route
              path={ROUTES_XLOGS.unposted_sessions.batch_post}
              element={
                <UnpostedSessionsBatchPostProvider>
                  <UnpostedSessionsBatchPostView />
                </UnpostedSessionsBatchPostProvider>
              }
            />

            <Route
              path={ROUTES_XLOGS.reports.progressReport}
              element={
                <UserManagementProvider>
                  <ProgressReportingContextProvider>
                    <ProgressReporting />
                  </ProgressReportingContextProvider>
                </UserManagementProvider>
              }
            />
            <Route
              path={ROUTES_XLOGS.reports.progressSummary}
              element={
                <ProgressReportingContextProvider>
                  <ProgressSummary />
                </ProgressReportingContextProvider>
              }
            />

            {/* Build */}
            <Route path="/xlogs/build" element={<BuildInfo />} />
            {/* 404 */}
            <Route path="*" element={<PageNotFound />} />
          </Routes>
            </Suspense>
          </CalendarContextProvider>

          {/* MODALS | This is where we house the globally accessible, minimum necessary modals controlled by associated Redux slices. */}
          <Suspense fallback={null}>
            <ErrorEntriesModal />
            {/* TODO: Are the below two modals deprecated? */}
            <ThankYouModal />
            <SimpleAttentionModal />
            {/* TODO: Consider moving the below modals to their associated features */}
            <ProviderNotFoundError />
            <RemoveProviderConfirmationModal />
            <RMTSUpdateModal open={showRMTSModal} onClose={handleCloseRMTSModal} />
            <CampusSelectionModal
              open={showCampusModal}
              onUpdateCampus={handleUpdateCampus}
              onRemindLater={handleRemindLater}
            />
          </Suspense>
          <GlobalSpeechToText />
          </NavLayout>
      </CssBaseline>
    </>
  );
}

export default App;
